/* Interview Clozo — shared interactions & animations */
(function () {
  'use strict';

  const ready = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* Inject shared components (skipped when pages ship with baked-in nav/footer) */
  async function injectComponents() {
    const isFile = location.protocol === 'file:';
    const navTarget = document.getElementById('navbar-root');
    const footTarget = document.getElementById('footer-root');
    if (!navTarget && !footTarget) return; // already baked inline by bake-components.sh
    if (isFile) {
      if (navTarget) navTarget.innerHTML = navFallback();
      if (footTarget) footTarget.innerHTML = footerFallback();
      return;
    }
    try {
      const [navHtml, footHtml] = await Promise.all([
        navTarget ? fetch('/components/navbar.html').then(r => r.text()) : Promise.resolve(''),
        footTarget ? fetch('/components/footer.html').then(r => r.text()) : Promise.resolve('')
      ]);
      if (navTarget) navTarget.innerHTML = navHtml;
      if (footTarget) footTarget.innerHTML = footHtml;
    } catch (e) {
      if (navTarget) navTarget.innerHTML = navFallback();
      if (footTarget) footTarget.innerHTML = footerFallback();
    }
  }

  function navFallback() {
    return `<nav class="navbar"><div class="navbar-inner"><a href="/" class="logo-lockup"><img src="/assets/brand/icon.svg" alt="Interview Clozo"><span class="logo-text"><span class="logo-word">Interview Clozo</span><span class="logo-tag">By Clozo.ai</span></span></a><button class="nav-toggle" aria-label="Toggle menu"><span></span><span></span><span></span></button><div class="navbar-links"><a href="/">Home</a><div class="nav-dropdown"><a href="/features.html">Product</a><div class="nav-dropdown-menu"><a href="/features.html"><span class="nav-label">Features</span></a><a href="/how-it-works.html"><span class="nav-label">How it Works</span></a></div></div><a href="/pricing.html">Pricing</a><a href="/blog/">Blog</a><a href="/support.html">Support</a><a href="/login.html" class="navbar-login">Log in</a><a href="/pricing.html#pricing" class="navbar-cta" data-checkout="monthly" data-provider="stripe" data-trial data-entry="nav">Start Free Trial</a></div></div></nav>`;
  }
  function footerFallback() {
    return `<footer class="footer"><div class="container"><div class="footer-bottom"><span>&copy; 2026 Interview Clozo. All rights reserved.</span><div class="footer-bottom-links"><a href="/privacy.html">Privacy</a><a href="/terms.html">Terms</a></div></div></div></footer>`;
  }

  /* The drawer takes over at max-width:1040px in styles.css. Both the tap
     handler and the hover intent derive from this ONE query (negating it for
     desktop) so no fractional viewport width — 1040.5px at an odd zoom level —
     can fall between two hand-written queries and leave neither active. */
  const drawerQuery = window.matchMedia('(max-width: 1040px)');
  const inDrawerMode = () => drawerQuery.matches;

  /* Navbar scroll + mobile toggle */
  function initNav() {
    const navbar = document.querySelector('.navbar');
    const toggle = document.querySelector('.nav-toggle');
    const links = document.querySelector('.navbar-links');
    if (navbar) {
      const onScroll = () => navbar.classList.toggle('scrolled', window.scrollY > 30);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll(); // set initial state (mid-page reloads)
    }
    if (toggle && links) {
      const closeMenu = () => {
        links.classList.remove('open');
        toggle.setAttribute('aria-expanded', 'false');
      };
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = links.classList.toggle('open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      // Close on Escape / outside click
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
      document.addEventListener('click', (e) => {
        if (links.classList.contains('open') && !e.target.closest('.navbar')) closeMenu();
      });
      // Links inside the menu close it on tap (dropdown parents are handled below)
      links.querySelectorAll('a').forEach(a => {
        if (a.parentElement.classList.contains('nav-dropdown')) return;
        a.addEventListener('click', closeMenu);
      });
      // Mobile menu: first tap on a dropdown parent expands its submenu, second tap navigates.
      // Desktop (hover) behavior is pure CSS and untouched.
      links.querySelectorAll('.nav-dropdown:not(.navbar-account) > a').forEach(a => {   // X4: "Log in" is a plain link in the drawer
        a.addEventListener('click', (e) => {
          if (!inDrawerMode()) return;
          const dd = a.parentElement;
          if (!dd.classList.contains('open')) {
            e.preventDefault();
            links.querySelectorAll('.nav-dropdown.open').forEach(o => { if (o !== dd) o.classList.remove('open'); });
            dd.classList.add('open');
          }
        });
      });
    }
    initDropdownHoverIntent(links);
    applyAuthNav();
  }

  /* Desktop dropdowns: hover intent.
     Bare :hover used to open the menu, but a cursor travelling from the trigger
     down to an item leaves the group for a moment — across the gap above the
     menu, or on a diagonal towards an item that is not directly below — and the
     menu vanished before it could be clicked. Once this runs it sets .js-nav on
     <html>; styles.css then stops opening on :hover and .is-open below becomes
     the single source of truth, HELD for a grace period after the pointer
     leaves so the trip always survives. The CSS bridge
     (.nav-dropdown-menu::before) covers the gap itself; this covers the rest. */
  function initDropdownHoverIntent(links) {
    if (!links) return;
    const HOVER_GRACE_MS = 320;
    const groups = Array.from(links.querySelectorAll('.nav-dropdown'));
    if (!groups.length) return;
    const SWITCH_DELAY_MS = 140;
    const timers = new WeakMap();
    const triggerOf = (dd) => dd.querySelector(':scope > a');
    // Tell the CSS that JS is driving: bare :hover stops opening menus and
    // .is-open below becomes the single source of truth.
    document.documentElement.classList.add('js-nav');

    const setExpanded = (dd, open) => {
      const t = triggerOf(dd);
      if (t) t.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    const closeNow = (dd) => {
      clearTimeout(timers.get(dd));
      timers.delete(dd);
      dd.classList.remove('is-open');
      setExpanded(dd, false);
    };
    const openNow = (dd) => {
      clearTimeout(timers.get(dd));
      timers.delete(dd);
      groups.forEach(o => { if (o !== dd) closeNow(o); });
      dd.classList.add('is-open');
      setExpanded(dd, true);
    };
    const closeSoon = (dd) => {
      clearTimeout(timers.get(dd));
      timers.set(dd, setTimeout(() => closeNow(dd), HOVER_GRACE_MS));
    };
    /* Opening the FIRST menu is instant. Switching to another one waits for the
       pointer to settle, so a cursor merely crossing a neighbouring trigger on
       its way to the menu already open does not hand the menu over to it. */
    const openIntent = (dd) => {
      clearTimeout(timers.get(dd));
      timers.delete(dd);
      // Re-entering the group that is already open also cancels any switch that
      // a fly-over of a neighbour had queued up.
      if (dd.classList.contains('is-open')) {
        groups.forEach(o => { if (o !== dd && !o.classList.contains('is-open')) { clearTimeout(timers.get(o)); timers.delete(o); } });
        return;
      }
      if (!groups.some(g => g.classList.contains('is-open'))) { openNow(dd); return; }
      timers.set(dd, setTimeout(() => openNow(dd), SWITCH_DELAY_MS));
    };

    groups.forEach(dd => {
      setExpanded(dd, false);
      // Touch taps are handled by the mobile open/close logic — never by hover.
      dd.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'touch' || inDrawerMode()) return;
        openIntent(dd);
      });
      dd.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'touch' || inDrawerMode()) return;
        closeSoon(dd);
      });
      // Keyboard: CSS :focus-within shows the menu; keep aria in step with it.
      dd.addEventListener('focusin', () => setExpanded(dd, true));
      dd.addEventListener('focusout', (e) => {
        if (!dd.contains(e.relatedTarget)) setExpanded(dd, false);
      });
      // Following a link closes at once — no menu left hanging over the new page.
      dd.querySelectorAll('.nav-dropdown-menu a').forEach(a => {
        a.addEventListener('click', () => closeNow(dd));
      });
    });

    // Escape closes the open group and hands focus back to its trigger.
    links.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const dd = e.target.closest && e.target.closest('.nav-dropdown');
      if (!dd) return;
      closeNow(dd);
      const t = triggerOf(dd);
      if (t) t.focus();
    });

    // Crossing the breakpoint must not leave a desktop menu pinned open.
    const onBreakpoint = () => groups.forEach(closeNow);
    if (drawerQuery.addEventListener) drawerQuery.addEventListener('change', onBreakpoint);
    else if (drawerQuery.addListener) drawerQuery.addListener(onBreakpoint);
  }

  /* Auth-aware navbar: logged-in users get an "Account" link instead of Log in / Start Free Trial, and html.is-auth
     (styles.css .auth-in / .auth-out) is set. Idempotent. auth.js adoptSession() calls it again (window.__clozoAuthNav)
     when a page signs the visitor in without a reload (R17 on success.html, R19 on welcome.html). */
  function applyAuthNav() {
    try {
      if (!window.InterviewAuth || typeof window.InterviewAuth.token !== 'function' || !window.InterviewAuth.token()) return;
      document.documentElement.classList.add('is-auth');
      const join = document.querySelector('.navbar .navbar-join'); if (join) join.remove();   // owner message 16: signed in, no "Create account"
      // R059: signed in, the navbar's Affiliates entries open the affiliate dashboard (one click); signed out they keep the program page
      document.querySelectorAll('.navbar a[href="/affiliates/"]').forEach((a) => { a.href = '/affiliates/portal.html#overview'; });
      if (document.querySelector('.navbar a.navbar-login[href="/account.html"]')) return;
      const login = document.querySelector('.navbar .navbar-login');
      const cta = document.querySelector('.navbar .navbar-cta');
      const anchor = cta || login;
      if (!anchor) return;
      const account = document.createElement('a');
      account.href = '/account.html';
      account.className = 'navbar-login';
      account.textContent = 'Account';
      anchor.replaceWith(account);
      if (cta && login) (login.closest('.navbar-account') || login).remove();   // X4: the "Log in" menu goes with it
    } catch (e) { /* auth.js absent or storage blocked — leave navbar as-is */ }
  }
  window.__clozoAuthNav = applyAuthNav;

  /* Scroll reveal — GSAP when available, IO + inline styles otherwise.
     Grid siblings reveal with a per-index stagger. Honors prefers-reduced-motion. */
  function initReveals() {
    /* A hero is never hidden or tweened (D8): it is the page's LCP element, and
       holding it at opacity 0 until GSAP arrived from a CDN and finished a 0.7s
       tween put pricing's LCP past 2s. styles.css shows it without JS too. */
    const items = Array.from(document.querySelectorAll('.reveal, .reveal-left, .reveal-scale'))
      .filter(el => !el.closest('.hero'));
    if (!items.length) return;
    const showFinal = (el) => { el.style.opacity = '1'; el.style.transform = 'none'; };
    if (prefersReducedMotion()) { items.forEach(showFinal); return; }
    /* Grids whose children arrive one after another.

       .pricing-grid is deliberately NOT in this list. Its cards are one priced
       row — the CSS goes to the trouble of giving every card shared row tracks so
       their prices, notes and pay rails sit on the same lines — and an 80ms
       per-index stagger undid that for the whole 0.7s of the tween: each card
       ran its own y:24 -> 0, so while the row was arriving the card tops
       were each at a different height (measured peak spread 9.04px on
       pricing.html, 9.06px on index.html, settling to 0). It looked exactly
       like a mis-built row to anyone who scrolled past, or screenshotted, at
       the wrong moment. With no delay the cards share one tween and the
       row arrives level, at every frame. */
    const GRID_SEL = '.bento, .grid, .offer-wall, .steps, .anchor-strip, .stats-bar, .testimonial-grid, .blog-grid';
    const delayOf = (el) => {
      const parent = el.parentElement;
      if (!parent || !parent.matches(GRID_SEL)) return 0;
      const siblings = Array.from(parent.children).filter(c => c.matches('.reveal, .reveal-left, .reveal-scale'));
      return Math.max(0, siblings.indexOf(el)) * 80; // ms
    };
    /* Cards that share a row are triggered off the ROW, not off themselves, so
       they cannot start a frame apart: one card ahead of its neighbours by a
       sub-pixel (a scrollbar appearing, a fractional zoom) would otherwise
       cross 'top 85%' on an earlier frame and lift alone. Cards that stack at
       the 1-up breakpoint keep triggering off the row they are in, so a lower
       card still animates when it is reached rather than finishing
       off-screen. */
    const rowOf = (el) => {
      const parent = el.parentElement;
      if (!el.matches('.pricing-card') || !parent || !parent.matches('.pricing-grid')) return el;
      const peers = Array.from(parent.children).filter(c => c.matches('.pricing-card'));
      const first = peers.find(c => Math.abs(c.offsetTop - el.offsetTop) < 4);
      return first || el;
    };
    if (window.gsap && window.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      items.forEach(el => {
        let from = { opacity: 0, y: 24 };
        let to = { opacity: 1, y: 0 };
        if (el.classList.contains('reveal-left')) { from = { opacity: 0, x: -30 }; to = { opacity: 1, x: 0 }; }
        if (el.classList.contains('reveal-scale')) { from = { opacity: 0, scale: 0.96 }; to = { opacity: 1, scale: 1 }; }
        gsap.fromTo(el, from, { ...to, duration: 0.7, ease: 'power3.out', delay: delayOf(el) / 1000, scrollTrigger: { trigger: rowOf(el), start: 'top 85%', once: true } });
      });
    } else {
      // Same contract without GSAP: one observer entry per row, every member of
      // that row revealed on the frame the row is reached.
      const groups = new Map();
      items.forEach(el => {
        const lead = rowOf(el);
        if (!groups.has(lead)) groups.set(lead, []);
        groups.get(lead).push(el);
      });
      const io = new IntersectionObserver((entries) => {
        entries.forEach(en => {
          if (!en.isIntersecting) return;
          io.unobserve(en.target);
          (groups.get(en.target) || [en.target]).forEach(el => {
            const d = delayOf(el);
            el.style.transition = `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${d}ms, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${d}ms`;
            showFinal(el);
            el.classList.add('visible');
          });
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
      Array.from(groups.keys()).forEach(lead => io.observe(lead));
    }
  }

  /* Book styling, off the critical path (BOOK-SYSTEM §1.10). A block with
     data-bk-css="/assets/css/book-show.css?v=…" is styled by book-show.css and,
     on pages that do not link it, book.css (URL = the navbar's stamped book.js
     data-book-css). Both are added once: at init only if a block is already
     within 2.5 viewports or the URL hash points into one; otherwise when a block
     comes within 1.5 viewports (above or below), or at the first scroll /
     pointerdown / keydown / touchstart / hashchange (an in-page "#book" jump
     finds it already loading). A late sheet is switched on with the scroll
     anchored to what is on screen (no visible jump). */
  function initBookCss() {
    const blocks = document.querySelectorAll('[data-bk-css]');
    if (!blocks.length) return;
    const ok = (u) => !!u && /^\/assets\/css\/book(-show)?\.css(\?v=[A-Za-z0-9._-]+)?$/.test(u);
    const tag = document.querySelector('script[data-book-css]');
    const urls = [tag && tag.getAttribute('data-book-css'), blocks[0].getAttribute('data-bk-css')].filter(ok);
    let done = false;
    const H = document.documentElement;
    const fixedIn = (e) => { for (; e && e !== document.body; e = e.parentElement) if (getComputedStyle(e).position === 'fixed') return true; return false; };
    const anchor = () => {                                       // what the visitor is looking at (not the book block, not fixed chrome)
      const pts = document.elementsFromPoint ? document.elementsFromPoint(window.innerWidth / 2, window.innerHeight / 2) : [];
      for (const e of pts) {
        if (e === document.body || e === H) break;
        if (!e.closest('[data-bk-css]') && !e.querySelector('[data-bk-css]') && !fixedIn(e)) return e;
      }
      return null;
    };
    // late = attached after init (a scroll, IO, a press): the block may sit ABOVE the viewport, and its restyle (≈1,080 px on
    // pricing) moved everything on screen, because Chrome's scroll anchoring does not absorb it. So a late sheet is fetched
    // as media=print (not applied), then switched on and the scroll corrected in ONE task: no frame shows the jump.
    const add = (late) => {
      if (done) return; done = true;
      const ls = [];
      urls.forEach((u) => {
        const f = u.split('?')[0];                               // home.bundle.css says data-bundles="… book.css" (tools/bundles.js)
        if (document.querySelector('link[href^="' + f + '"], link[data-bundles~="' + f.split('/').pop() + '"]')) return;
        const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = u;
        if (late === true) l.media = 'print';
        ls.push(l); document.head.appendChild(l);
      });
      if (late !== true || !ls.length) return;
      let n = ls.length;
      const on = () => {
        if (--n > 0) return;
        const a = anchor(), t0 = a ? a.getBoundingClientRect().top : 0;
        H.style.overflowAnchor = 'none';
        ls.forEach((l) => { l.media = 'all'; });
        if (a) {
          const d = a.getBoundingClientRect().top - t0;          // forces the restyled layout; same task, so no paint between
          if (Math.abs(d) > 1) { H.style.scrollBehavior = 'auto'; window.scrollBy(0, d); H.style.scrollBehavior = ''; }   // never animated
        }
        requestAnimationFrame(() => { H.style.overflowAnchor = ''; });
      };
      ls.forEach((l) => { l.addEventListener('load', on, { once: true }); l.addEventListener('error', on, { once: true }); });
    };
    const later = () => add(true);
    const near = (b) => b.getBoundingClientRect().top < window.innerHeight * 2.5;
    let hashEl = null;
    try { hashEl = location.hash.length > 1 && document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch (_) {}
    // P4d J2: a deep link whose target sits BELOW the first lazily styled book block takes the late path (fetched as print,
    // switched on + scroll-corrected in one task), so book-show.css geometry above the anchor never moves what the visitor
    // landed on (landing #how / #plans / #faq: CLS 0.0648 → 0.0001, VISUAL-SYSTEM §2.2)
    if (hashEl && !hashEl.closest('[data-bk-css]') && (blocks[0].compareDocumentPosition(hashEl) & 4)) { later(); return; }
    if (!('IntersectionObserver' in window) || (hashEl && hashEl.closest('[data-bk-css]')) || Array.prototype.some.call(blocks, near)) { add(); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { later(); io.disconnect(); } }, { rootMargin: '150% 0px 150% 0px' });
    blocks.forEach((b) => io.observe(b));
    // + the first scroll: a jump / scrollbar drag / fling past the block styles it on the way (anchored), so a reload's
    // restored offset always matches the styled layout that init() gives it (P4b-fix-sync, gate r1 pricing reload)
    ['scroll', 'pointerdown', 'keydown', 'touchstart', 'hashchange'].forEach((ev) => window.addEventListener(ev, later, { once: true, passive: true }));
  }

  /* The book, opened (d23 §4.4): figures below the fold rest closed
     (.is-armed) and open once >= 35 % visible (.is-open; book-show.css runs
     the 0.76 s transform-only reveal). In view at init, reduced motion or no
     IntersectionObserver: .is-open at once, no motion. */
  function initBookOpen() {
    const figs = document.querySelectorAll('[data-bk3d]');
    if (!figs.length) return;
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) { figs.forEach(f => f.classList.add('is-open')); return; }
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-open'); io.unobserve(e.target); }
    }), { threshold: 0.35 });
    figs.forEach(f => {
      if (f.getBoundingClientRect().top < window.innerHeight) { f.classList.add('is-open'); return; }
      f.classList.add('is-armed'); io.observe(f);
    });
  }

  /* Animated counters — HTML ships the final value; JS counts up to it when in view */
  function initCounters() {
    const counters = document.querySelectorAll('[data-count]');
    if (!counters.length) return;
    const setFinal = (el) => {
      const target = +el.dataset.count;
      el.textContent = (el.dataset.prefix || '') + target.toLocaleString() + (el.dataset.suffix || '');
    };
    if (prefersReducedMotion()) { counters.forEach(setFinal); return; }
    const run = (el) => {
      const target = +el.dataset.count;
      const suffix = el.dataset.suffix || '';
      const prefix = el.dataset.prefix || '';
      const dur = 1500;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = Math.floor(eased * target);
        el.textContent = prefix + val.toLocaleString() + suffix;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    if (window.gsap && window.ScrollTrigger) {
      counters.forEach(el => {
        ScrollTrigger.create({ trigger: el, start: 'top 85%', once: true, onEnter: () => run(el) });
      });
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(en => { if (en.isIntersecting) { run(en.target); io.unobserve(en.target); } });
      }, { threshold: 0.5 });
      counters.forEach(el => io.observe(el));
    }
  }

  /* FAQ accordion.

     Every .faq-item carries a stable #faq-… id and the JSON-LD on these pages
     cites those ids as each Question's url, so a deep link has to arrive OPEN:
     .faq-answer is a grid row at 0fr until .open is set, which means an
     un-opened anchor scrolls the reader to a heading whose answer has zero
     height. openTarget() is therefore driven from four places — the button,
     the hash at load, every later hashchange, and same-page anchor clicks,
     which never fire hashchange because initAnchors() preventDefault()s them. */
  function initFAQ() {
    const buttons = Array.from(document.querySelectorAll('.faq-question'));
    if (!buttons.length) return;

    /* One writer for both halves of the state: the class the CSS reads, and
       the attribute assistive tech reads. */
    const setOpen = (item, open) => {
      item.classList.toggle('open', open);
      item.querySelector('.faq-question')?.setAttribute('aria-expanded', String(open));
    };

    const closeSiblings = (item) => {
      item.closest('.faq-list')?.querySelectorAll('.faq-item.open')
        .forEach(other => { if (other !== item) setOpen(other, false); });
    };

    buttons.forEach((btn, i) => {
      const item = btn.closest('.faq-item');
      if (!item) return;
      const answer = item.querySelector('.faq-answer');
      if (answer) {
        if (!answer.id) answer.id = (item.id || 'faq-' + i) + '-answer';
        btn.setAttribute('aria-controls', answer.id);
      }
      btn.setAttribute('aria-expanded', String(item.classList.contains('open')));
      btn.addEventListener('click', () => {
        const open = item.classList.contains('open');
        closeSiblings(item);
        setOpen(item, !open);
      });
    });

    /* Open the item a fragment addresses and put it under the fixed navbar
       ([id] carries scroll-margin-top: 90px, which scrollIntoView honors).
       The scroll waits a frame so it measures the EXPANDED item, and so it
       lands after the scroll initAnchors() runs for that same click. The
       reveal transform is cleared too: a deep-linked answer must not arrive at
       opacity 0 waiting on a scroll that has already happened. */
    const openTarget = (id, scroll) => {
      if (!id) return false;
      let el = null;
      try { el = document.getElementById(id); } catch (e) { el = null; }
      const item = el && el.closest ? el.closest('.faq-item') : null;
      if (!item) return false;
      closeSiblings(item);
      setOpen(item, true);
      item.style.opacity = '1';
      item.style.transform = 'none';
      if (scroll) requestAnimationFrame(() => item.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      return true;
    };

    const idFromHash = (hash) => {
      const raw = String(hash || '').replace(/^[^#]*#/, '');
      if (!raw) return '';
      try { return decodeURIComponent(raw); } catch (e) { return raw; } // malformed %-escape: use it verbatim
    };

    const openFromHash = () => openTarget(idFromHash(location.hash), true);

    window.addEventListener('hashchange', openFromHash);
    document.addEventListener('click', (e) => {
      const a = e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (a) openTarget(idFromHash(a.getAttribute('href')), true);
    });
    openFromHash();
  }


  /* ------------------------------------------------------------------------
     Auto-fitting headline lines.

     Every [data-fit] element is a line that must stay ONE line: the hero
     lockup (icon + wordmark), the two tagline lines, the headline setup, and
     the headline's rotating line. CSS sizes them; this shrinks a line only if
     its natural, unwrapped width does not fit the column it sits in.

     Why measure rather than pick a size per breakpoint: the rotating line is
     "You <phrase>." and every phrase is a different length, so any size that
     fit one phrase wrapped another — and the wrap arrived and left as the
     typewriter ran, moving the whole page by a line every few seconds. The
     old fix reserved the tallest phrase's height, which held the page still by
     donating 150px of a 667px screen to blank space. Measuring against the
     LONGEST phrase instead picks the size once, so the line neither wraps nor
     resizes for as long as the animation runs.

     [data-fit] elements are flex items in a centred column, so their border
     box is their content width even when it overflows — that is the natural
     width we compare against the parent's content box.
     ------------------------------------------------------------------------ */
  function initFitLines() {
    const lines = Array.from(document.querySelectorAll('[data-fit]'));
    if (!lines.length) return;

    const longestPhrase = (el) => {
      const tw = el.querySelector('[data-typewriter]');
      if (!tw) return null;
      let phrases;
      try { phrases = JSON.parse(tw.dataset.typewriter); } catch (e) { return null; }
      if (!Array.isArray(phrases) || !phrases.length) return null;
      return { node: tw, text: phrases.reduce((a, b) => (b.length > a.length ? b : a), '') };
    };

    const fit = () => {
      if (twPin) twPin.unpin();   // the typewriter's pinned line is measured as the plain centred line it stands for
      for (const el of lines) {
        const parent = el.parentElement;
        if (!parent) continue;
        const ps = getComputedStyle(parent);
        const avail = parent.clientWidth - parseFloat(ps.paddingLeft || 0) - parseFloat(ps.paddingRight || 0) - 1;
        if (!(avail > 0)) continue;

        // Measure at the size CSS asked for, with the widest content in place.
        el.style.fontSize = '';
        el.style.minHeight = '';
        const swap = longestPhrase(el);
        const restore = swap ? swap.node.textContent : null;
        if (swap) swap.node.textContent = swap.text;

        const base = parseFloat(getComputedStyle(el).fontSize);
        const box = el.getBoundingClientRect();
        const natural = box.width;
        if (swap) swap.node.textContent = restore;

        if (!base || !natural) continue;
        if (natural > avail) {
          el.style.fontSize = (base * (avail / natural)) + 'px';
          // Keep the line's CSS-size height: scripts.js is deferred (behind GSAP), so this often runs after the first
          // paint, and a line that got a few px shorter pulled the rest of the hero up (home CLS 0.09 at 390 px).
          el.style.minHeight = box.height + 'px';
        }
      }
      if (twPin) twPin.pin();
    };

    fit();
    // The first pass runs on the fallback face, which is a different width from
    // Inter, Space Grotesk and Nevera. Re-fit once the real faces are in.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit).catch(() => {});
    let t;
    const refit = () => { clearTimeout(t); t = setTimeout(fit, 120); };
    window.addEventListener('resize', refit);
    window.addEventListener('orientationchange', refit);
  }

  /* Typewriter.
     The rotating line must not move anything in LAYOUT while it types (INT-F1, home CLS): centred by text-align,
     every character re-centred the line, so "You", the phrase and "." moved on every keystroke — a layout shift
     each (about 0.005 of the home page's audited phone CLS), and the first keystroke emptied the phrase the
     page had just painted. With JS the line is pinned (.tw-on, styles.css): laid out left-aligned in a box as
     wide as its WIDEST phrase, "." (and the caret, its ::after) positioned at the end of the typed text by a
     transform, the whole line centred by a transform. What a visitor sees is exactly the centred line as
     before; a transform is not a layout shift. initFitLines() unpins the line while it measures and re-pins it.
     The typing starts from the phrase already on the page (hold, delete, next) instead of clearing it. */
  let twPin = null;
  function initTypewriter() {
    const el = document.querySelector('[data-typewriter]');
    if (!el) return;
    let phrases = [];
    try { phrases = JSON.parse(el.dataset.typewriter); } catch (e) { return; }
    if (!Array.isArray(phrases) || !phrases.length) return;
    // Line height and wrapping are owned by initFitLines(); this only types (and pins, below).
    const line = el.parentElement, dot = el.nextElementSibling, first = line && line.firstElementChild;
    // Fractional geometry in the line's own CSS px: rect offsets from the line's box (its transform cancels out),
    // divided by any scale an ancestor animation applies (the reveal); offsetLeft/Top would round to whole px.
    let W = 0;
    const geo = () => {
      const L = line.getBoundingClientRect(), k = L.width / (parseFloat(getComputedStyle(line).width) || L.width) || 1;
      const R = (n) => n.getBoundingClientRect();
      return { L, k, x: (n, edge) => (R(n)[edge] - L.left) / k, y: (n) => (R(n).top - L.top) / k, w: (n) => R(n).width / k };
    };
    const place = () => {                                   // the typed text's end -> "." and the centring transform
      const g = geo(), end = g.x(el, 'right'), start = g.x(first, 'left');
      dot.style.transform = 'translateX(' + end + 'px)';
      line.style.transform = 'translateX(' + ((W - (end - start + g.w(dot))) / 2 - start) + 'px)';
    };
    if (line && line.hasAttribute('data-fit') && dot && first && first !== el && !dot.nextElementSibling) {
      twPin = {
        unpin() { line.classList.remove('tw-on'); line.style.width = line.style.transform = dot.style.transform = dot.style.top = ''; },
        pin() {
          // measured unpinned (centred, in flow): the row "." sits on, and the widest the line's box gets over every
          // phrase (its min-width or the widest phrase) — pinned, the box keeps that width, so it never moves either
          const shown = el.textContent, top = geo().y(dot);
          W = 0;
          for (const p of phrases) { el.textContent = p; W = Math.max(W, parseFloat(getComputedStyle(line).width) || 0); }
          el.textContent = shown;
          line.classList.add('tw-on');
          line.style.width = W + 'px';
          dot.style.top = top + 'px';
          place();
        }
      };
      twPin.pin();
    }
    let idx = 0, char = el.textContent === phrases[0] ? phrases[0].length : 0, deleting = false;
    const type = () => {
      const current = phrases[idx];
      el.textContent = current.substring(0, char);
      if (twPin) place();
      if (!deleting && char < current.length) { char++; setTimeout(type, 45); }
      else if (deleting && char > 0) { char--; setTimeout(type, 20); }
      else { deleting = !deleting; if (!deleting) idx = (idx + 1) % phrases.length; setTimeout(type, deleting ? 1800 : 400); }
    };
    type();
  }

  /* ------------------------------------------------------------------------
     Demo players (design/COMPONENTS.md §1). The player is its own file
     (data-player on every figure.demo, stamped by stamp-assets.sh) and is
     fetched only when a demo is about to be seen: a demo within 300px of the
     viewport after the visitor's first scroll, or one that is already on
     screen at load (after the load event). A page nobody scrolls costs these
     few lines and nothing else: no poster, no video, no player.
     A demo that is already on screen at DOMContentLoaded (tall desktops:
     1728x1000, 2560x1300) gets its poster now, at high priority, so the LCP
     is the poster and not a late image after load + player; the player
     reuses that <img>. Hidden steps-group members and closed FAQ answers
     get none (their posters wait for the player, as before).
     ------------------------------------------------------------------------ */
  function initDemos() {
    const figs = document.querySelectorAll('figure.demo[data-player]');
    if (!figs.length) return;
    let done = false, armed = false, near = false, io = null;
    figs.forEach((f) => {
      const st = f.querySelector('.demo-stage'), r = st && st.getBoundingClientRect();
      if (!r || !r.height || r.top >= window.innerHeight || r.bottom <= 0 || f.closest('.faq-item:not(.open)')) return;
      const cs = getComputedStyle(f);
      if (cs.visibility === 'hidden') return;
      const c = f.querySelector('[role="tab"][aria-selected="true"]') || f;
      const p = c.getAttribute('data-poster-' + (cs.getPropertyValue('--cut').trim() === 'd' ? 'desktop' : 'mobile'));
      if (!p) return;
      const im = document.createElement('img');
      im.alt = '';
      im.setAttribute('aria-hidden', 'true');
      im.setAttribute('data-boot', '');
      im.setAttribute('fetchpriority', 'high');
      im.src = p;
      st.prepend(im);
    });
    const load = () => {
      if (done) return;
      done = true;
      if (io) io.disconnect();
      const s = document.createElement('script');
      s.src = figs[0].getAttribute('data-player');
      s.async = true;
      document.head.appendChild(s);
    };
    const later = () => (document.readyState === 'complete' ? load() : window.addEventListener('load', load, { once: true }));
    if (!('IntersectionObserver' in window)) return later();
    io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (!en.isIntersecting) continue;
        const r = en.boundingClientRect;
        if (armed) return load();
        if (r.top < window.innerHeight && r.bottom > 0) return later();
        near = true;
      }
    }, { rootMargin: '300px 0px' });
    figs.forEach((f) => io.observe(f));
    window.addEventListener('scroll', () => { armed = true; if (near) load(); }, { passive: true, once: true });
  }

  /* Magnetic buttons */
  function initMagnetic() {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('.btn-magnetic').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
      });
      btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
    });
  }

  /* Particle canvas — loops indefinitely; static single frame under reduced motion */
  function initParticles() {
    const canvas = document.getElementById('particle-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w, h, particles = [];
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', resize);
    resize();
    const count = Math.min(80, Math.floor((w * h) / 15000));
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5, a: Math.random() * 0.5 + 0.2
      });
    }
    const still = prefersReducedMotion();
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p, i) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(34,211,238,${p.a})`; ctx.fill();
        if (i % 6 === 0) {
          for (let j = i + 1; j < particles.length; j += 6) {
            const q = particles[j];
            const dx = p.x - q.x, dy = p.y - q.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 100) {
              ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
              ctx.strokeStyle = `rgba(167,139,250,${0.15 * (1 - dist / 100)})`; ctx.stroke();
            }
          }
        }
      });
      if (!still) requestAnimationFrame(draw);
    };
    draw();
  }

  /* Starfield for inner pages */
  function initStarfield() {
    const el = document.querySelector('.starfield');
    if (!el) return;
    for (let i = 0; i < 60; i++) {
      const s = document.createElement('span');
      s.style.left = Math.random() * 100 + '%';
      s.style.top = Math.random() * 100 + '%';
      s.style.animationDelay = Math.random() * 3 + 's';
      s.style.opacity = Math.random() * 0.5 + 0.2;
      el.appendChild(s);
    }
  }

  /* Parallax orbs: intentionally removed — ambient.css runs an infinite CSS
     transform animation on .gradient-orb, so inline transforms here were dead code. */

  /* Smooth scroll for anchors */
  function initAnchors() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    });
  }

  /* ---- Redesign v2 engines ---- */

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Marquee: duplicate track content for a seamless loop (clone is aria-hidden) */
  function initMarquees() {
    document.querySelectorAll('.marquee').forEach(m => {
      const track = m.querySelector('.marquee-track');
      if (!track || track.dataset.cloned) return;
      const originalCount = track.children.length;
      track.innerHTML += track.innerHTML;
      Array.from(track.children).slice(originalCount).forEach(c => c.setAttribute('aria-hidden', 'true'));
      track.dataset.cloned = '1';
    });
  }

  /* "Featured on" badges (home): the markup holds ONE copy of the strip, so every directory link is in the page once.
     When the strip first comes into view, the looping copy is appended (aria-hidden, out of the tab order) and only then
     does the loop start (.is-loop, styles.css), so the strip never jumps. Reduced motion: no copy, no loop (it wraps). */
  function initBadgeLoop() {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    document.querySelectorAll('.saascity-badges-track[data-loop]').forEach(track => {
      const io = new IntersectionObserver((entries) => {
        if (!entries.some(e => e.isIntersecting) || track.classList.contains('is-loop')) return;
        io.disconnect();
        const copy = document.createDocumentFragment();
        Array.from(track.children).forEach(c => {
          const k = c.cloneNode(true);
          k.setAttribute('aria-hidden', 'true');
          k.setAttribute('tabindex', '-1');
          copy.appendChild(k);
        });
        track.appendChild(copy);
        track.classList.add('is-loop');
      });
      io.observe(track);
    });
  }

  /* Book pages (S9): a button[data-full] opens its full-size image in a modal <dialog> instead of linking to the .webp
     file. Esc, the close button or a click on the backdrop closes it; focus goes back to the page that opened it. */
  function initFullView() {
    let dlg = null, img = null, opener = null;
    const FULL = /^\/assets\/images\/[a-z0-9/_-]+\.(?:webp|png|jpe?g)(?:\?v=[A-Za-z0-9._-]+)?$/;
    document.addEventListener('click', (ev) => {
      const t = ev.target && ev.target.closest ? ev.target.closest('[data-full]') : null;
      const src = t && t.getAttribute('data-full');
      if (!src || !FULL.test(src)) return;
      ev.preventDefault();
      if (!dlg) {
        dlg = document.createElement('dialog');
        dlg.className = 'fullview';
        const x = document.createElement('button');
        x.type = 'button';
        x.className = 'fullview-x';
        x.setAttribute('aria-label', 'Close');
        x.textContent = '\u00d7';
        x.addEventListener('click', () => dlg.close());
        img = document.createElement('img');
        img.decoding = 'async';
        dlg.append(x, img);
        dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
        dlg.addEventListener('close', () => { img.removeAttribute('src'); if (opener && opener.isConnected) opener.focus({ preventScroll: true }); opener = null; });
        document.body.appendChild(dlg);
      }
      const thumb = t.querySelector('img');
      opener = t;
      img.alt = (thumb && thumb.getAttribute('alt')) || '';
      img.src = src;
      dlg.setAttribute('aria-label', img.alt || 'Full-size page');
      if (typeof dlg.showModal === 'function') dlg.showModal(); else window.open(src, '_blank', 'noopener');
    });
  }

  /* Offer wall: rotating spotlight glow */
  function initOfferWalls() {
    document.querySelectorAll('.offer-wall').forEach(wall => {
      const cards = wall.querySelectorAll('.offer-card');
      if (cards.length < 2 || prefersReducedMotion()) return;
      let current = -1;
      setInterval(() => {
        if (current >= 0) cards[current].classList.remove('spotlight');
        let next = Math.floor(Math.random() * cards.length);
        if (next === current) next = (next + 1) % cards.length;
        cards[next].classList.add('spotlight');
        current = next;
      }, 2600);
    });
  }

  /* Status boards: fabricated "verified Nm ago" jitter removed — the static
     markup is rendered as-is. */

  /* Scroll-pinned steps: activate step items as they enter the viewport center */
  function initStepPins() {
    document.querySelectorAll('.step-pin').forEach(pin => {
      const items = pin.querySelectorAll('.step-item');
      if (!items.length) return;
      const activate = () => {
        const mid = window.innerHeight * 0.45;
        items.forEach(item => {
          const r = item.getBoundingClientRect();
          item.classList.toggle('active', r.top < mid && r.bottom > window.innerHeight * 0.2);
        });
      };
      window.addEventListener('scroll', activate, { passive: true });
      activate();
      // First item starts active
      if (!pin.querySelector('.step-item.active')) items[0].classList.add('active');
    });
  }

  /* 3D tilt on hover.

     The lean toward the pointer is capped by the card's HEIGHT. Under
     perspective(800px) an edge moves by roughly (half-height)^2 x angle / 800,
     so the same 3.5deg that shifts a 300px card's edge by 2px shifted the foot
     of a 1,440px plan pane by 40-58px — enough that a click aimed at Start Free
     Trial landed on the neighbouring pay button, or on the pane itself (which is
     a Pay with Card target). Only cards taller than TILT_TALL_PX are affected — every ordinary
     card on the site (the tallest is ~600px) tilts exactly as it always has —
     and those keep the same motion at an angle scaled to TILT_REF_PX / height,
     which holds the pay rail's drift under its 10px button gap.

     Mouse-out restores the card's RESTING transform rather than clearing it. A
     scroll-revealed card rests on an inline transform (GSAP leaves
     translate(0px, 0px), the fallback leaves none); clearing it let the
     stylesheet's pre-reveal `html.js .reveal { transform: translateY(24px) }`
     back in, so every hovered card settled 24px below its neighbours for good. */
  const TILT_TALL_PX = 700;
  const TILT_REF_PX = 240;
  function initTilt() {
    if (window.matchMedia('(pointer: coarse)').matches || prefersReducedMotion()) return;
    document.querySelectorAll('.card-tilt').forEach(card => {
      const isReveal = card.matches('.reveal, .reveal-left, .reveal-scale');
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        const h = card.offsetHeight;
        const k = h > TILT_TALL_PX ? TILT_REF_PX / h : 1;
        const rx = ((e.clientY - r.top) / r.height - 0.5) * -7 * k;
        const ry = ((e.clientX - r.left) / r.width - 0.5) * 7;
        card.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
      });
      card.addEventListener('mouseleave', () => { card.style.transform = isReveal ? 'none' : ''; });
    });
  }

  // html.is-smooth (styles.css) turns CSS smooth scrolling on only after the reload / back scroll restore is done
  const smoothOn = () => setTimeout(() => document.documentElement.classList.add('is-smooth'), 600);
  if (document.readyState === 'complete') smoothOn(); else window.addEventListener('load', smoothOn, { once: true });

  ready(() => {
    // Non-nav inits run immediately, in parallel with component injection.
    initReveals();
    initBookCss();
    initBookOpen();
    initCounters();
    initFAQ();
    initFitLines();
    initTypewriter();
    initMagnetic();
    initParticles();
    initStarfield();
    initAnchors();
    initMarquees();
    initBadgeLoop();
    initFullView();
    initOfferWalls();
    initStepPins();
    initTilt();
    initDemos();
    // Nav-dependent init waits for injection (no-op when nav is already baked in).
    injectComponents().then(initNav);
  });
})();

/* =============================================================================
   STICKY CTA BAR — ported from the clozo.ai main site (initStickyCta + the
   custom plan dropdown), so both properties behave identically.

   Visibility: hidden while the hero is on screen (the hero already has a CTA)
   and hidden again once the real footer is in view, so it never sits on top of
   the footer links. Visible in between — desktop and mobile alike.

   Plan selection: clozo.ai rewrites one button's href. This site checks out via
   checkout.js, which reads each [data-checkout] button's own attributes at click
   time, so we render every plan's button up front and toggle [hidden].
   ========================================================================== */
(function () {
  'use strict';

  function initStickyCta() {
    var bar = document.getElementById('stickyCta');
    if (!bar) return;

    /* --- visibility --------------------------------------------------- */
    var hero = document.querySelector('.hero');
    var footer = document.querySelector('.footer');
    var heroVisible = !!hero;
    var footerVisible = false;

    function updateVisibility() {
      if (heroVisible || footerVisible) { bar.classList.remove('visible'); }
      else { bar.classList.add('visible'); }
    }

    if ('IntersectionObserver' in window) {
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (hero && entry.target === hero) heroVisible = entry.isIntersecting;
          if (footer && entry.target === footer) footerVisible = entry.isIntersecting;
        });
        updateVisibility();
      }, { threshold: 0 });
      if (hero) obs.observe(hero);
      if (footer) obs.observe(footer);
    }

    // Pages without a hero (404, auth, account): nothing to defer to, so reveal at once; the scroll fallback below only runs where IntersectionObserver is unavailable.
    if (!hero) {
      heroVisible = false;
      if ('IntersectionObserver' in window && footer) {
        updateVisibility();
      } else {
        window.addEventListener('scroll', function onScroll() {
          if (window.pageYOffset > 200) {
            updateVisibility();
            window.removeEventListener('scroll', onScroll);
          }
        }, { passive: true });
      }
    }

    /* --- custom plan dropdown ----------------------------------------- */
    var dd = document.getElementById('planDropdown');
    var trigger = document.getElementById('planDropdownTrigger');
    var menu = document.getElementById('planDropdownMenu');
    if (!dd || !trigger || !menu) return;

    function close() {
      dd.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      dd.classList.toggle('open');
      trigger.setAttribute('aria-expanded', String(dd.classList.contains('open')));
    });

    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); trigger.click(); }
      if (e.key === 'Escape') { close(); }
    });

    var items = Array.prototype.slice.call(menu.querySelectorAll('.plan-dropdown-item'));
    items.forEach(function (item, idx) {
      function choose() {
        items.forEach(function (i) {
          i.classList.remove('selected');
          i.setAttribute('aria-selected', 'false');
        });
        item.classList.add('selected');
        item.setAttribute('aria-selected', 'true');

        var name = item.querySelector('.dd-item-name').textContent;
        var price = item.querySelector('.dd-item-price').textContent;
        trigger.querySelector('.dd-label').innerHTML =
          '<span class="dd-price">' + name + '</span> ' + price;

        var val = item.getAttribute('data-value');
        var ctas = document.querySelectorAll('.sticky-cta-bar [data-plan-cta]');
        Array.prototype.forEach.call(ctas, function (btn) {
          btn.hidden = btn.getAttribute('data-plan-cta') !== val;
        });

        // Drop any stale checkout error left from a previous selection.
        var err = document.querySelector('.sticky-cta-bar [data-checkout-error]');
        if (err) { err.textContent = ''; err.style.display = ''; }

        close();
        trigger.focus();
      }

      item.addEventListener('click', choose);
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); (items[idx + 1] || items[0]).focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); (items[idx - 1] || items[items.length - 1]).focus(); }
        else if (e.key === 'Escape') { e.preventDefault(); close(); trigger.focus(); }
      });
    });

    document.addEventListener('click', function (e) {
      if (!dd.contains(e.target)) close();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyCta);
  } else {
    initStickyCta();
  }
})();

/* =============================================================================
   download_click — one delegated listener covering every page.

   Fires when a visitor heads for the app: a direct .dmg link, anything under
   /download/, or the install guide (which is where most download journeys
   actually start — 146 links point at it against 7 direct .dmg links).

   Deliberately tolerant: window.clozo may be undefined because the analytics
   script was blocked by an ad blocker or a CSP, and a throw inside a click
   handler cancels the navigation. Losing an event is acceptable; losing the
   click is not. Hence the guard and the try/catch around the whole body.
   ========================================================================== */
(function () {
  'use strict';

  var DOWNLOAD_RE = /(\.dmg($|[?#])|\/download\/|\/install\.html($|[?#]))/i;

  document.addEventListener('click', function (e) {
    try {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;

      var href = a.getAttribute('href') || '';
      if (!DOWNLOAD_RE.test(href)) return;

      if (!window.clozo || typeof window.clozo.track !== 'function') return;
      window.clozo.track('download_click', {
        href: href,
        page: window.location.pathname
      });
    } catch (_) {
      /* never let analytics break a navigation */
    }
  }, true);   // capture phase: still fires if a handler below calls stopPropagation
})();

/* =============================================================================
   P4d visual system J1 (visual/final/visual-system.js, verbatim; VISUAL-SYSTEM §2.1). Deferred, NOT render-blocking.
   1. initPaintCss: attach assets/css/aurora-paint.css (the PAINT tier) after `load` AND the settled LCP (fix r1: the last
      LCP candidate 400 ms old, or the first scroll/press/key, capped at 3 s), in idle time — never before the LCP (OD26). The URL comes from <meta name="x-paint-css" content="/assets/css/aurora-paint.css?v=…"> in components/head.html,
      which stamp-assets.sh content-hashes like any other /assets/ reference. No-JS visitors get the <noscript> link.
      FIN gates-fix-2: also any page sheet named by a data-paint-css="/assets/css/<name>.css?v=…" attribute, same timing.
   2. initLive: .is-live on .hero / .cta-section while on screen, so the two desktop loops (hero aurora drift, CTA conic)
      never run off-screen (JUDGE-eng §9.1-9). Phones have no loop at all (CSS).
   3. refresh ScrollTrigger once whenever ANY stylesheet finishes loading late (aurora-paint.css, book-show.css, book.css):
      a late sheet that changes a block's height strands the GSAP reveals below it at opacity 0 (found by aurora). One
      capture-phase `load` listener on document catches <link> loads without touching F-fix-tested initBookCss. */
(function () {
  var raf = 0;
  function refreshST() {
    if (raf || !window.ScrollTrigger) return;
    raf = requestAnimationFrame(function () { raf = 0; window.ScrollTrigger.refresh(); });
  }
  document.addEventListener('load', function (e) {
    var t = e.target;
    if (t && t.tagName === 'LINK' && /stylesheet/i.test(t.rel)) refreshST();
  }, true);
  // WebKit: a refresh under html.is-smooth (scroll-behavior: smooth) animates GSAP's scrollTo(0,0) and the restoring
  // scrollTo(0,y) does not stop it, so the page glides to the top (demo-qa WEBKIT W1). Every refresh, ours or GSAP's own
  // (resize, rotation), runs with smooth scrolling off.
  if (window.ScrollTrigger) {
    var sb = '';
    window.ScrollTrigger.addEventListener('refreshInit', function () { var h = document.documentElement; sb = h.style.scrollBehavior; h.style.scrollBehavior = 'auto'; });
    window.ScrollTrigger.addEventListener('refresh', function () { document.documentElement.style.scrollBehavior = sb; });
  }

  function initPaintCss() {
    var m = document.querySelector('meta[name="x-paint-css"]');
    var u = m && m.getAttribute('content');
    var us = [];
    if (u && /^\/assets\/css\/aurora-paint\.css(\?v=[A-Za-z0-9._-]+)?$/.test(u) && !document.querySelector('link[href^="/assets/css/aurora-paint.css"]')) us.push(u);
    // FIN gates-fix-2: + a page's own late sheet, named by data-paint-css="/assets/css/<name>.css?v=…" (platforms/index: its
    // below-the-fold rules, kept out of the document so it fits the first round trip; the boxes on screen at load stay inline)
    Array.prototype.forEach.call(document.querySelectorAll('[data-paint-css]'), function (e) {
      var p = e.getAttribute('data-paint-css');
      if (p && /^\/assets\/css\/[a-z0-9-]+\.css(\?v=[A-Za-z0-9._-]+)?$/.test(p) && us.indexOf(p) < 0 && !document.querySelector('link[href^="' + p.split('?')[0] + '"]')) us.push(p);
    });
    if (!us.length) return;
    var go = function () {
      us.forEach(function (x) {
        var l = document.createElement('link');
        l.rel = 'stylesheet'; l.href = x;
        document.head.appendChild(l);
      });
    };
    var idle = function () { ('requestIdleCallback' in window) ? requestIdleCallback(go, { timeout: 1200 }) : setTimeout(go, 150); };
    // fix r1 (P4d review copyperf): `load` fires BEFORE the LCP paint on text-LCP pages (terms: load 173 ms, LCP 193 ms), so
    // the sheet entered Lighthouse's LCP graph. Wait until the last LCP candidate is 400 ms old (debounced), or the first
    // scroll / press / key, capped at 3 s after load; browsers without the LCP entry type keep the plain idle-after-load path.
    var afterLoad = function () {
      var done = false, t = 0;
      var fire = function () { if (done) return; done = true; clearTimeout(t); idle(); };
      var types = window.PerformanceObserver && PerformanceObserver.supportedEntryTypes;
      if (!types || types.indexOf('largest-contentful-paint') < 0) { fire(); return; }
      setTimeout(fire, 3000);
      ['scroll', 'pointerdown', 'keydown'].forEach(function (ev) { window.addEventListener(ev, fire, { once: true, passive: true }); });
      try {
        new PerformanceObserver(function (list) {
          if (!list.getEntries().length || done) return;
          clearTimeout(t); t = setTimeout(fire, 400);
        }).observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) { fire(); }
    };
    if (document.readyState === 'complete') afterLoad(); else window.addEventListener('load', afterLoad, { once: true });
  }

  // .is-live while on screen: the hero, the closing CTA and every [data-live] block (key-features.css plays its motion
  // only then; this used to be key-features.js, one more request on the home page)
  function initLive() {
    if (!('IntersectionObserver' in window)) return;
    var els = document.querySelectorAll('.hero, .cta-section, [data-live]');
    if (!els.length) return;
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { e.target.classList.toggle('is-live', e.isIntersecting); });
    });
    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  function start() { initPaintCss(); initLive(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
